import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RiCheckboxCircleFill, RiCheckLine, RiPlaneLine, RiBuildingLine, RiTrophyLine, RiMedalLine, RiPriceTag3Line } from '@remixicon/react';
import Button from '@components/ui/Button';
import { TrustScore } from '../../trust-score';
import { TrustJetPlane, TrustJetLogoV2 } from '@assets/images';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/constants';
import { formatPrice } from '@/utils';
import { Skeleton } from '@components/ui/Skeleton';
import { getAircraftModelName, getOperatorWithLocation } from '@/features/tails/utils';
/**
 * BookingConfirmed Page Component
 * Displays booking confirmation with aircraft details after successful payment
 */
const BookingConfirmed = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const { userProfile } = useAuthStore();
  const quoteId = searchParams.get('quoteId');
  const isTokenBasedCheckout = searchParams.get('token');
  const optionId = searchParams.get('option');
  const referenceId = searchParams.get('referenceId');

  // Fetch quote details using React Query (must be before early return)
  const { data: quote, isLoading, error: quoteError } = useQuery({
    queryKey: ['quote', quoteId, isTokenBasedCheckout, optionId],
    queryFn: async () => {
      let data;
      if (isTokenBasedCheckout) {
        // Use token-based API for public access
        data = await apiClient.getQuoteByIdWithToken(quoteId, isTokenBasedCheckout);
      } else {
        // Use authenticated API
        data = await apiClient.getQuoteById(quoteId);
      }
      return data;
    },
    enabled: !!quoteId,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time page loads
    retry: 2,
    retryDelay: 1000,
  });

  console.log("confirmedquote", quote);

  // Extract the selected bid option from quote data (works for both token and authenticated)
  const quoteBidOption = useMemo(() => {
    if (!quote) return null;
    // If optionId is provided, find the matching bid option
    if (optionId) {
      for (const bidItem of (quote.bid_items || [])) {
        const options = bidItem.bid?.bid_options || bidItem.bid_options || [];
        const matchingOption = options.find(
          option => option.bid_option_id === optionId
        );
        if (matchingOption) return matchingOption;
      }
    }
    // Fallback: return the first bid option available
    const firstBidItem = quote.bid_items?.[0];
    return firstBidItem?.bid?.bid_options?.[0] || firstBidItem?.bid_options?.[0] || null;
  }, [quote, optionId]);


  // Fetch organization name
  const orgId = userProfile?.org_id;
  const { data: organization } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => apiClient.getOrganizationById(orgId),
    enabled: !!orgId && !isTokenBasedCheckout,
  });

  // Extract aircraft data: prefer quote API data, fallback to store
  const aircraft = quoteBidOption || {};
  const aircraftDetails = aircraft.aircraft || {};
  const operator = aircraft.operator || aircraftDetails.operator || {};
  const features = aircraft.aircraft_features || {};

  const handleFinish = async() => {
    // Invalidate quotes cache to refresh data
    await queryClient.invalidateQueries({ queryKey: ['quotes'] });
    navigate(ROUTES.QUOTES.path);
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${TrustJetPlane})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-xl">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <img src={TrustJetLogoV2} alt="TrustJet" className="h-14" />
        </div>

        {/* Transparent Container Box */}
        <div className="bg-white/20 backdrop-blur-md rounded-3xl p-6 w-full">
          {/* Confirmation Title */}
          {quote && (
            <>
              <h1 className="text-3xl md:text-4xl font-semibold text-white mb-2">
                Your booking is confirmed!
              </h1>
              {organization?.name && (
                <p className="text-white/80 mb-4 text-sm">
                  Thank you for booking with <span className="font-medium text-white">{organization.name}</span>.
                </p>
              )}
              {referenceId && (
                <div className="flex gap-2 bg-green-50 text-green-800 rounded-full px-4 py-2 mb-4 w-full">
                  <RiCheckboxCircleFill className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm font-medium">Your booking number is {referenceId}</span>
                </div>
              )}
            </>
          )}

          {/* Booking Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl w-full overflow-hidden">
            {(isLoading || !quote) ? (
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-28" />
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-36" />
                  </div>
                  <Skeleton className="h-12 w-12 rounded-full" />
                </div>
                <Skeleton className="h-7 w-24 rounded-full" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
                <div className="border-t border-gray-100 pt-4 space-y-2">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              </div>
            ) : (
              /* Aircraft Info Section */
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {aircraftDetails.tail_number || 'N123GT'}
                    </h2>
                    <div className="flex items-center gap-1 text-gray-600 text-sm mt-1">
                      <RiPlaneLine className="w-4 h-4" />
                      <span>{getAircraftModelName(aircraftDetails)}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600 text-sm mt-1">
                      <RiBuildingLine className="w-4 h-4" />
                      <span>
                        {getOperatorWithLocation(operator)}
                      </span>
                    </div>
                  </div>

                </div>

                {/* Price */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-accent text-primary-500 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <RiPriceTag3Line className="w-4 h-4" /> <span>{formatPrice(parseFloat(((aircraft.final_price || aircraft.total_price || quote?.total_amount || 0) * 1.04).toFixed(2)))}</span>
                  </div>
                </div>

                {/* Feature Badges */}
                <div className="flex flex-wrap gap-2">

                  {operator?.argus_rating && operator?.argus_rating!="No" &&
                  <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
                    <RiTrophyLine className="h-4 w-4 text-gray-600"/> {operator?.argus_rating}
                  </span>}

                  {operator?.wyvern_rating && operator?.wyvern_rating!="No" &&
                  <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
                    <RiMedalLine className="h-4 w-4 text-gray-600"/> WINGMAN
                  </span>
                  }

                  {features?.no_aog &&
                  <span className={`flex px-2 py-1 bg-green-100 text-green-700 text-xs rounded-2xl font-medium`}>
                    <RiCheckLine className="h-4 w-4 text-gray-600"/> NO AOG
                  </span>
                  }
                </div>

                {/* Amenities */}
                <div className="mt-2">

                  <p className="text-xs text-gray-500 mb-2">Amenities</p>

                  {features?.custom_features && features?.custom_features?.length > 0 ? (
                      <div className="grid grid-cols-1 gap-1">
                        {features?.custom_features.map((feature, index) => (
                          <div key={index} className="flex border-b py-1">
                            <div className="w-10 h-7 flex items-center justify-center flex-shrink-0">
                              <RiCheckLine className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                              <div className="text-sm text-gray-500">{feature}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                  ): (
                    <p className="text-xs text-gray-400">No amenities included.</p>
                  )}

                </div>
              </div>
            )}
          </div>

          {/* Finish Button - Separate Box */}
          {!isTokenBasedCheckout && quote &&
          <div className="mt-4">
            <Button
              variant="secondary"
              className="w-full py-3 rounded-full flex items-center justify-center gap-2 !bg-white text-gray-800 font-medium"
              onClick={handleFinish}
            >
              Finish
              <RiCheckLine className="w-5 h-5" />
            </Button>
          </div>
          }
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmed;
